import React, { useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';
import { AddComment as BaseAddComment } from '@producer/ui-kit';

import { CommentInput } from './comment-input';
import { CommentContext } from '../../core/contexts/comment-context/comment.context';
import { authStore } from '../../core/stores/auth-store';
import { extractMentionedUserIds } from '../../lib/utils/extract-mentions';

import type { CommentDraft } from '@producer/ui-kit';
import type { Comment } from '../../app/entities/comment';

type NewComment = Pick<Comment, 'text' | 'timestamp' | 'page' | 'annotations' | 'mentionedUserIds'>;

interface AddCommentProps {
  onAddComment: (comment: NewComment) => Promise<string>;
  playerRef?: React.RefObject<HTMLVideoElement | null>;
  timeTagRef?: React.RefObject<HTMLSpanElement>;
  mimeType?: string;
  pdfCurrentPage?: number;
}

/**
 * The shared comment box, plus what belongs to a signed-in author: mentions taken from the
 * editor's HTML, and selecting the comment that was just posted.
 */
export const AddComment: React.FC<AddCommentProps> = observer(
  ({ onAddComment, mimeType, playerRef, pdfCurrentPage = 0, timeTagRef }) => {
    const [, setSearchParams] = useSearchParams();

    const user = authStore.currentUser;
    const { selectComment } = useContext(CommentContext);

    const handleAddComment = async (draft: CommentDraft) => {
      const mentionedUserIds = extractMentionedUserIds(draft.text);

      const comment: NewComment = {
        ...draft,
        ...(mentionedUserIds.length > 0 && { mentionedUserIds }),
      } as NewComment;

      const commentId = await onAddComment(comment);

      if (commentId) {
        selectComment?.({ _id: commentId, ...comment } as Comment);
        setSearchParams((params) => {
          params.set('commentId', commentId);
          return params;
        });
      }

      return commentId;
    };

    return (
      <BaseAddComment
        onAddComment={handleAddComment}
        user={{ avatar: user?.avatar, initials: user?.initials }}
        mimeType={mimeType}
        playerRef={playerRef}
        pdfCurrentPage={pdfCurrentPage}
        timeTagRef={timeTagRef}
        renderInput={(props) => <CommentInput {...props} />}
      />
    );
  },
);
